import { z } from "zod/v4";

import { canonicalJsonString } from "../types/canonicalJsonString";
import { type OperatorSpec, operatorSpecSchema } from "./OperatorSpec";
import {
	type FromFilterSpec,
	type FromSpec,
	fromFilterSpecSchema,
	fromSpecSchema,
	permissionClasses,
} from "./from";
import { orderedTopKSpecSchema } from "./orderedTopK";

export const querySpecSchema = z.custom<[FromSpec, ...OperatorSpec[]]>((val) => {
	if (!Array.isArray(val) || val.length === 0) {
		return false;
	}

	// Check first element is a FromSpec
	const [first, ...rest] = val;
	fromSpecSchema.parse(first);

	// Check rest are OperatorSpecs
	for (const op of rest) {
		operatorSpecSchema.parse(op);
	}

	return true;
});
export type QuerySpec = z.infer<typeof querySpecSchema>;

export const contentId = (spec: QuerySpec): string => {
	const specString = canonicalJsonString(spec);
	const contentId = Buffer.from(specString).toString("base64");
	return contentId;
};

export const compoundQueryKey = (
	input: ({ contentId: string } | { querySpec: QuerySpec }) & { permissionSpec: FromFilterSpec }
): string => {
	const cid = "contentId" in input ? input.contentId : contentId(input.querySpec);
	return `${cid}:${Buffer.from(canonicalJsonString(input.permissionSpec)).toString("base64")}`;
};

export const registerQueryRequestSpecSchema = z.object({
	spec: querySpecSchema,
	subquerySpecs: z.array(querySpecSchema),
	finalizer: orderedTopKSpecSchema.optional(),
	permissionClass: permissionClasses.optional(),
});
export type registerQueryRequestSpec = z.infer<typeof topLevelQuerySpecSchema>;

export const topLevelQuerySpecSchema = z.object({
	spec: querySpecSchema,
	subquerySpecs: z.array(querySpecSchema),
	finalizer: orderedTopKSpecSchema.optional(),
	permissionFilter: fromFilterSpecSchema.optional(),
});
export type TopLevelQuerySpec = z.infer<typeof topLevelQuerySpecSchema>;

export const topLevelQuerySpec = (
	spec: QuerySpec,
	...subquerySpecs: QuerySpec[]
): TopLevelQuerySpec => ({
	spec,
	subquerySpecs,
	finalizer: undefined,
	permissionFilter: { type: "read_all_rows" } as const,
});
